import * as React from 'react';

import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { Box } from '@leagueplatform/genesis-core';
import { useScrollTopOnLocationChange } from '@leagueplatform/web-common';
import {
  APP_NAV_DEFAULT_HEIGHT,
  AuthedWebviewModal,
  DraggableLiveChat,
  FEATURE_FLAGS,
  INTERNAL_BASE_ROUTES,
  LinkHandler,
  LoadingSpinner,
  QueryParamModal,
  useSsoDeepLink,
  ImpersonationMasonryBanner,
  ErrorBoundary,
} from '@highmark-web/highmark-common';
import { useLocation } from 'react-router-dom';
import { useGetAppFeatureFlag } from 'hooks/use-get-app-feature-flag.hook';
import { AppNavBarComponent } from './nav.component';
import { AllMyHealthFooter } from './footer/allmyhealth-footer.component';

const MAIN_CONTENT_MIN_HEIGHT = `calc(100vh - ${APP_NAV_DEFAULT_HEIGHT}px)`;

const ExcludeChatBubbleRoutes = [
  getModulePath(LEAGUE_MODULE_NAMES.healthJourney),
  getModulePath(LEAGUE_MODULE_NAMES.healthPrograms),
  getModulePath(LEAGUE_MODULE_NAMES.healthProgress),
  getModulePath(LEAGUE_MODULE_NAMES.healthJourneyHistory),
  INTERNAL_BASE_ROUTES.COORDINATION_OF_BENEFITS_BASE,
];

export type PrivateBasePageProps = {
  enableLiveChat?: boolean;
};

export const PrivateBasePage: React.FC<PrivateBasePageProps> = ({
  enableLiveChat,
  children,
}) => {
  useScrollTopOnLocationChange();
  useSsoDeepLink(LinkHandler);

  const { isEnabled: isLiveChatNavigateAwayEnabled } = useGetAppFeatureFlag(
    FEATURE_FLAGS.LIVE_CHAT_NAVIGATE_AWAY,
  );

  const location = useLocation();
  // Get first path string before non character value (/, ?, etc)
  const basePath = location?.pathname?.match(/^\/[\w-]+/) ?? ['/'];
  const isChatExcludedPath = ExcludeChatBubbleRoutes.includes(basePath[0]);

  const isChatBubbleEnabled = Boolean(
    !isChatExcludedPath && enableLiveChat && isLiveChatNavigateAwayEnabled,
  );

  return (
    <>
      <AppNavBarComponent />
      <AuthedWebviewModal />
      <QueryParamModal />
      <ImpersonationMasonryBanner />
      <Box
        as="main"
        id="main-content"
        css={{
          position: 'relative',
          flex: '1',
          overflowX: 'hidden',
          // This is so any page will take up the whole screen regardless of content
          minHeight: MAIN_CONTENT_MIN_HEIGHT,
          paddingBottom: '$twoAndHalf',
        }}
      >
        <ErrorBoundary
          contextLocation="BasePage ErrorBoundary"
          styleErrorProps={{ minHeight: MAIN_CONTENT_MIN_HEIGHT }}
        >
          <React.Suspense fallback={<LoadingSpinner />}>
            {children}
          </React.Suspense>
        </ErrorBoundary>
      </Box>
      <AllMyHealthFooter />
      {isChatBubbleEnabled && <DraggableLiveChat />}
    </>
  );
};
